import React, { useMemo, useContext } from 'react';
import { getLocale, shallowEqual, useSelector } from 'umi';
import { Card } from 'tea-component';
import PlatformCardItem from './PlatformCardItem';
import PieChart from '@/pages/dashboard/components/Chart/components/PieChart';
import { getEchartTooltipText } from '@/pages/dashboard/components/EchartsBubble';
import { FilterContext } from '../PageContainer/context';
import { createProgressListByDimension, updateProgressListByDimension } from './_utils';
import '@/pages/dashboard/assets/scss/page/Overlay/components/cartContext.scss';
import { getDataByKey } from '../TrendChartCo/_utils/common';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import './index.scss';
import classNames from 'classnames';
import { LANGUAGE_EN } from '@home/_config/config';
import type { ISelectorState } from '../../types/dashboard';
import type { IProgressItem } from './type';
import { dynamicString } from '@home/utils/dynamicString';
import { getName } from '@home/utils/common';

interface IConfigBase {
  titleEn: string;
  titleCn: string;
  metricKey?: string;
  formatRule: string;
  dimension?: string;
  dataKey?: string;
  defaultItemCount?: number;
  preUnit?: string; // 前置单位（如 $）
  endUnit?: string; // 后置单位（如 %）
}

interface IDimensionsCardProps {
  chartConfig: IConfigBase;
  percentageBarConfig: IConfigBase & {
    type?: string;
    decimal?: number;
    defaultSelectedList?: string[];
  };
  dataListConfig: IConfigBase;
  isChange?: boolean;
  data?: any;
  className?: string;
  style?: React.CSSProperties;
  height?: number;
  changeHeight?: number;
}

export default function MultiViewDimensionCard(props: IDimensionsCardProps) {
  const { chartConfig, percentageBarConfig, dataListConfig, data, className, style, isChange, height, changeHeight } = props;
  const isEN = getLocale() === LANGUAGE_EN;
  const locale = getLocale();
  
  // 获取上下文和全局状态
  const contextObj = useContext(FilterContext);
  const { filterVal, extraSelector: contextExtraSelector } = contextObj || {};
  const systemId = (filterVal?.system as string[]) || ['255'];
  
  const { gameCode = '', globalSelector } = useSelector(
    (state: ISelectorState) => ({
      gameCode: state?.dashboard?.currentGame?.game_code,
      globalSelector: state?.dashboardSetting?.selector,
    }),
    shallowEqual
  );

  // 合并全局 selector 和 extraSelector
  const selector = useMemo(() => {
    if (!contextExtraSelector) {
      return globalSelector;
    }
    return {
      ...globalSelector,
      ...contextExtraSelector,
    };
  }, [globalSelector, contextExtraSelector]);

  const countryList = selector?.country_list || [];
  const platformList = useMemo(() => {
    const osList = (selector?.os_list ?? []).filter((item: any) => item?.os_id !== '255');
    const adList = (selector?.ad_format ?? []).filter((item: any) => item?.code !== '255');
    return [...osList, ...adList];
  }, [selector]);

  // 获取数据
  const { breakdown: platFormData = [] } = (getDataByKey(percentageBarConfig?.dataKey || '', data) || {}) || {};
  const dataListRawData = getDataByKey(dataListConfig?.dataKey || '', data);
  const { breakdown: dataListData = [] } = dataListRawData || {};
  
  // 获取圆环图数据
  const chartRawData = getDataByKey(chartConfig?.dataKey || '', data);
  const { breakdown: chartData = [] } = chartRawData || {};

  // 构造维度上下文（与 PlatformCardItem 保持一致）
  const dimensionContext = useMemo(() => ({
    selector,
    countryList,
    isEN,
    gameCode,
    systemId,
    platformList,
  }), [selector, countryList, isEN, gameCode, systemId, platformList]);

  // 构造 PlatformCardItem 所需的 dataSource
  const platformDataSource = {
    type: percentageBarConfig?.type || 'int',
    decimal: percentageBarConfig?.decimal || 0,
    formatRule: dynamicString(percentageBarConfig.formatRule, data) || 'thousandsInit',
    dimension: percentageBarConfig?.dimension || 'ad_format',
    defaultSelectedList: percentageBarConfig?.defaultSelectedList,
  };

  // 创建初始的 progressList（使用 PlatformCardItem 的逻辑）
  const disableProgressList = useMemo(() => {
    const { dimension = 'ad_format', defaultSelectedList } = platformDataSource;
    return createProgressListByDimension(dimension, defaultSelectedList, dimensionContext);
  }, [platformDataSource, dimensionContext]);

  // 更新 progressList（处理真实数据 + 国际化）- 有数据时按 platFormData 顺序，无数据时直接展示 disableProgressList
  const { progressList = [] } = useMemo(() => {
    const { dimension = 'ad_format' } = platformDataSource;

    // platFormData 无数据时，不按 platFormData 排序，直接使用 disableProgressList 顺序展示
    const hasPlatFormData = Array.isArray(platFormData) && platFormData.length > 0;
    if (!hasPlatFormData) {
      const finalProgressList = updateProgressListByDimension(
        dimension,
        disableProgressList,
        dimensionContext
      );
      return { progressList: finalProgressList };
    }

    // 创建 disableProgressList 的映射表（通过 name 查找）
    const progressMap = new Map<string, IProgressItem>();
    disableProgressList.forEach((item) => {
      progressMap.set(item.name, item);
    });

    // 按照 platFormData 的顺序构建 progressList
    const updatedProgressList: IProgressItem[] = platFormData.map((item: any) => {
      const name = item.name || item[dimension];
      const progressItem = progressMap.get(name);

      if (progressItem) {
        // 找到对应的 progressItem，更新数据
        return {
          ...progressItem,
          hasData: true,
          num: item.value !== '' && item.value !== null && item.value !== undefined
            ? item.value
            : '-',
          share: item.share_of_all || '',
        };
      }

      // 如果没有找到对应的 progressItem，创建一个新的（补充所有必需属性）
      const newItem: IProgressItem = {
        name,
        title: name,
        hasData: true,
        num: item.value !== '' && item.value !== null && item.value !== undefined
          ? item.value
          : '-',
        share: item.share_of_all || '',
        isOpacity: false,
        isBlackImg: false,
        disableIcon: '',
        isSelected: false,
      };
      return newItem;
    });

    // 应用国际化
    const finalProgressList = updateProgressListByDimension(
      dimension,
      updatedProgressList,
      dimensionContext
    );

    return { progressList: finalProgressList };
  }, [platFormData, disableProgressList, platformDataSource, dimensionContext]);

  // 获取标题的辅助函数
  const getConfigTitle = (config?: IConfigBase): string => {
    if (!config) return '';
    return getName(isEN, dynamicString(config.titleEn, data), dynamicString(config.titleCn, data));
  };

  // 三个配置的标题
  const chartTitle = getConfigTitle(chartConfig);
  const percentageBarTitle = getConfigTitle(percentageBarConfig);
  const dataListTitle = getConfigTitle(dataListConfig);

  // 按照中间进度条的顺序排列右侧数据列表 - 通过 name 映射
  const sortedDataList = useMemo(() => {
    // 获取要显示的数量，默认为 progressList 的长度
    const defaultItemCount = dataListConfig?.defaultItemCount || progressList.length;
    
    // 创建数据映射表（使用 name 作为 key）
    const dataMap = new Map();
    if (dataListData.length > 0) {
      dataListData.forEach((item: any) => {
        // 使用 name（如 'interstitial'、'rewarded'）作为 key
        const key = item.name || item[percentageBarConfig?.dimension || 'ad_format'];
        if (key) {
          dataMap.set(key, item.value);
        }
      });
    }
    
    // 按照 progressList 的顺序排列
    if (progressList.length > 0) {
      const result = progressList
        .slice(0, defaultItemCount)
        .map((progressItem: any) => {
          // 使用 progressItem.name（如 'rewarded'、'interstitial'）去匹配 dataListData
          const dimensionValue = progressItem.name;
          const value = dataMap.get(dimensionValue);
          
          return {
            dimension: dimensionValue,
            value: value !== undefined && value !== null && value !== '' ? value : '-',
          };
        });
      
      return result;
    }
    
    // 如果没有 progressList，但有 defaultItemCount，生成占位符
    if (defaultItemCount > 0) {
      const placeholders = Array.from({ length: defaultItemCount }, (_, index) => ({
        dimension: `placeholder-${index}`,
        value: '-',
      }));
      return placeholders;
    }
    return [];
  }, [progressList, dataListData, percentageBarConfig?.dimension, dataListConfig?.defaultItemCount]);

  // 准备圆环图数据 - 按照 progressList 的顺序，通过 name 映射
  const pieChartData = useMemo(() => {
    if (!progressList.length) {
      // 没有数据时，返回一个灰色的圆环
      return [{
        name: '', // 使用空字符串，避免显示文字
        value: 100,
        itemStyle: {
          color: '#E5E5E5', // 灰色
        },
      }];
    }
    
    // 创建圆环图数据映射（使用 name 作为 key）
    const chartDataMap = new Map();
    chartData.forEach((item: any) => {
      // 使用 name（如 'interstitial'、'rewarded'）作为 key
      const key = item.name || item.full_name?.toLowerCase();
      if (key) {
        chartDataMap.set(key, {
          value: Number(item.value) || 0,
          fullName: item.full_name,
        });
      }
    });
    
    // 按照 progressList 的顺序构建圆环图数据
    const validData = progressList
      .map((progressItem: any) => {
        // 使用 progressItem.name（如 'rewarded'、'interstitial'）去匹配 chartData
        const dataItem = chartDataMap.get(progressItem.name);
        const value = dataItem?.value || 0;
        
        return {
          name: progressItem.title, // 使用已经国际化的 title（激励视频/Rewarded）
          value,
          code: progressItem.name,   // 保存原始 code（rewarded）
          color: progressItem.color, // 使用 progressList 中的颜色（如果有）
        };
      })
      .filter((item) => item.value > 0); // 只显示有数值的项
    
    // 如果过滤后没有有效数据，返回灰色圆环
    if (validData.length === 0) {
      return [{
        name: '', // 使用空字符串，避免显示文字
        value: 100,
        itemStyle: {
          color: '#E5E5E5', // 灰色
        },
      }];
    }
    
    return validData;
  }, [progressList, chartData, locale]);

  // 圆环图的 tooltip formatter - 使用 getEchartTooltipText
  const pieChartFormatter = (param: any) => {
    const { name, value, color } = param;
    
    // 如果是无数据状态（name 为空字符串），不显示 tooltip
    if (!name || name === '') {
      return '';
    }
    
    const metricsNum = dataFormat({
      value,
      formatRule: dynamicString(chartConfig.formatRule, data)  || 'thousands2Decimal',
    });
    
    // 获取前后单位
    const preUnit = chartConfig?.preUnit || '';
    const endUnit = chartConfig?.endUnit || '';
    // 判断是否为有效数据：排除 '-'、null、undefined、空字符串
    const isValidValue = value !== '-' && value !== null && value !== undefined && value !== '' && metricsNum !== '-';
    const displayValue = isValidValue
      ? `${preUnit}${metricsNum}${endUnit}`
      : metricsNum;
    
    return getEchartTooltipText({
      title: chartTitle,
      metricsName: '',
      ratioName: '',
      hasRatio: true,
      data: [{
        name, // 这里的 name 已经是国际化后的值了（来自 progressItem.title）
        icon: '',
        metricsNum: displayValue, // 使用带单位的值
        ratioNum: `${param.percent}%`,
        color,
      }],
      isSort: false,
    });
  };

  // PieChart 的自定义配置
  const pieChartCustomConfig = (opts: any) => {
    // 检查是否为无数据状态
    const isNoData = pieChartData.length === 1 && pieChartData[0]?.name === '';
    
    return {
      ...opts,
      tooltip: {
        ...opts.tooltip,
        confine: false,
        position: [100, 50]
      },
      series: [
        {
          ...opts.series[0],
          radius: ['43%', '73%'], // 调整为圆环样式
          center: ['50%', '45%'],
          label: {
            show: false, // 不显示标签
          },
          labelLine: {
            show: false, // 不显示标签线
          },
          // 确保数据项也不显示标签
          data: opts.series[0].data?.map((item: any) => ({
            ...item,
            label: {
              show: false, // 每个数据项都不显示标签
            },
            labelLine: {
              show: false, // 每个数据项都不显示标签线
            },
          })),
          // 无数据时禁用所有交互
          ...(isNoData && {
            emphasis: {
              disabled: true, // 禁用高亮效果
            },
          }),
        },
      ],
      legend: {
        ...opts.legend,
        show: false, // 不显示图例
      },
      graphic: [], // 移除中间的文字
    };
  };

  return (
    <Card
      className={classNames('dbd-dimension-card dbd-overlay-card overlay-card', className)}
      style={{...style, height: isChange ? changeHeight : height}}
    >
      <Card.Body>
        <div className="dbd-dimension-card-container">
          {/* 左边区域 - 圆环图 */}
          <div className="dbd-dimension-card-section dbd-dimension-card-section-left">
            <div className="dbd-dimension-card-title">{chartTitle}</div>
            <div className="dbd-dimension-card-chart">
              <PieChart
                chartData={pieChartData}
                formatterFunc={pieChartFormatter}
                setOtherConfigFunc={pieChartCustomConfig}
                isShowLegend={false}
                height={100}
              />
            </div>
          </div>

          {/* 中间区域 - 进度条 */}
          <div className="dbd-dimension-card-section dbd-dimension-card-section-middle">
            <div className="dbd-dimension-card-title">{percentageBarTitle}</div>
            <div className="dbd-dimension-card-progress">
              <PlatformCardItem dataSource={platformDataSource} platFormData={platFormData} showShare={false} />
            </div>
          </div>

          {/* 右边区域 - 列表 */}
          <div className="dbd-dimension-card-section dbd-dimension-card-section-right">
            <div className="dbd-dimension-card-title">{dataListTitle}</div>
            <div className="dbd-dimension-card-list">
              {sortedDataList?.map((item: any, index: number) => {
                // 获取前后单位
                const preUnit = dataListConfig?.preUnit || '';
                const endUnit = dataListConfig?.endUnit || '';
                
                // 格式化数据
                const formattedValue = item.value !== '-' 
                  ? dataFormat({
                    value: item.value,
                    formatRule: dynamicString(dataListConfig.formatRule, data),
                  })
                  : '-';
                
                // 判断是否为有效数据：排除 '-'、null、undefined、空字符串
                const isValidValue = item.value !== '-' && item.value !== null && item.value !== undefined && item.value !== '' && formattedValue !== '-';
                
                return (
                  <div key={`${item.dimension}-${index}`} className="dbd-dimension-card-list-item">
                    {isValidValue
                      ? `${preUnit}${formattedValue}${endUnit}`
                      : formattedValue
                    }
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}